import secureApi from '../../utils/secureApi';
import { getImageUrl } from '../../utils/imageUrl';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =========================
// Tournament Team Registration Interfaces
// =========================

export interface TournamentTeamRegistration {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  thumbnail?: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  sports: string;
  ageCategories: string[];
  cancellationInfo?: {
    reason: string;
    date: string;
    _id: string;
  };
  // Mock data for team information (since API doesn't provide this)
  teamCount?: string;
  verifiedCount?: string;
}

// API response interface
export interface TournamentTeamRegistrationsApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: TournamentTeamRegistration[];
    totalCount: number;
  };
}

// =========================
// Helper Functions
// =========================

/**
 * Normalize logo URL to include S3 base URL if needed
 */
const normalizeLogoUrl = (tournament: any): string => {
  const candidate = tournament.logo || tournament.thumbnail || '';
  if (!candidate) return '';
  return getImageUrl(candidate);
};

/**
 * Generate mock team data for demonstration
 */
const generateMockTeamData = (tournament: any) => {
  // Generate random but realistic team data
  const teamCount = Math.floor(Math.random() * 15) + 5; // 5-20 teams
  const verifiedCount = Math.floor(teamCount * 0.8) + 1; // 80% verified + 1
  
  return {
    teamCount: teamCount.toString().padStart(2, '0'), // "08", "10", etc.
    verifiedCount: `${verifiedCount} Verified`
  };
};

/**
 * Map backend tournament shape to UI TournamentTeamRegistration type with safe defaults
 */
const normalizeTournamentTeamRegistration = (t: any): TournamentTeamRegistration => {
  const mockData = generateMockTeamData(t);
  
  return {
    _id: t._id,
    name: t.name || '',
    shortName: t.shortName || '',
    logo: normalizeLogoUrl(t),
    thumbnail: t.thumbnail || '',
    location: t.location || '',
    startDate: t.startDate || '',
    endDate: t.endDate || '',
    registrationStartDate: t.registrationStartDate || '',
    registrationEndDate: t.registrationEndDate || '',
    status: (t.status || 'upcoming') as TournamentTeamRegistration['status'],
    sports: t.sports || 'Football',
    ageCategories: Array.isArray(t.ageCategories) ? t.ageCategories : [],
    cancellationInfo: t.cancellationInfo || undefined,
    teamCount: mockData.teamCount,
    verifiedCount: mockData.verifiedCount,
  };
};

// =========================
// Tournament Team Registrations APIs
// =========================

/**
 * Fetch tournament team registrations with pagination
 * @param pageNum - Page number for pagination (default: 1)
 * @returns Promise<TournamentTeamRegistrationsApiResponse>
 */
export const getTournamentTeamRegistrations = async (pageNum: number = 1): Promise<TournamentTeamRegistrationsApiResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }


    const response = await secureApi.get<TournamentTeamRegistrationsApiResponse>(
      `/api/v1/tournament-registration/registered-tournaments`,
      {
        params: { pageNum }
      }
    );


    // Normalize the tournament data with mock team information
    if (response.data && response.data.paginatedResults) {
      response.data.paginatedResults = response.data.paginatedResults.map(normalizeTournamentTeamRegistration);
    }

    return response;
  } catch (error: any) {
    console.error('❌ Error fetching tournament team registrations:', error);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch tournament team registrations');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch tournament team registrations');
    }
  }
};

/**
 * Get tournament team registration by ID
 * @param tournamentId - The tournament ID
 * @returns Promise<TournamentTeamRegistration>
 */
export const getTournamentTeamRegistrationById = async (tournamentId: string): Promise<TournamentTeamRegistration> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }


    const response = await secureApi.get<TournamentTeamRegistrationsApiResponse>(
      `/api/v1/tournament-registration/registered-tournaments`
    );


    if (response.data && response.data.paginatedResults) {
      const tournament = response.data.paginatedResults.find(t => t._id === tournamentId);
      if (tournament) {
        return normalizeTournamentTeamRegistration(tournament);
      }
    }

    throw new Error('Tournament not found');
  } catch (error: any) {
    console.error('❌ Error fetching tournament team registration by ID:', error);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch tournament team registration');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch tournament team registration');
    }
  }
}; 

/**
 * Delete a tournament
 */
export const deleteTournament = async (tournamentId: string): Promise<void> => {
  try {
    
    const response = await secureApi.post(`/api/v2/tournaments/manage/delete-tournament/${tournamentId}`);
    
  } catch (error: any) {
    console.error('❌ Error deleting tournament:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Tournament not found');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to delete this tournament.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to delete tournament');
    }
  }
}; 